import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CameraCapture } from "@/components/common/camera-capture/camera-capture";
import type { CapturedPhoto } from "@/components/common/record-capture/captured-photo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useIssueRecord } from "@/hooks/use-issues";
import { recordImages } from "@/lib/record-images";
import type { ProjectIssue } from "@/types/api";
import { ISSUE_CAMERA_ATTR } from "./issue-camera-marker";

interface IssueUpdateCameraProps {
  projectId: string;
  issue: ProjectIssue;
  /** Done: every shot, oldest first, as the untouched camera frames. */
  onDone: (files: File[]) => void;
  /** Closed keeping nothing — no shots were taken, or the discard was confirmed. */
  onCancel: () => void;
  /** The camera's denied / no-camera fallback: open the library instead. */
  onUseLibrary: () => void;
}

/**
 * The update sheet's camera: several shots in a row with the issue's first
 * record photo ghosted over the live view, handed back to the sheet on Done.
 *
 * Mounted only while open, so every exit unmounts it — which is where the
 * shots' previews are released, the ghost's toggle comes back on, and the
 * shots themselves are dropped unless Done already handed them over. See
 * [[issue-update-sheet.spec]] § Photos.
 */
export function IssueUpdateCamera({
  projectId,
  issue,
  onDone,
  onCancel,
  onUseLibrary,
}: IssueUpdateCameraProps) {
  const [shots, setShots] = useState<CapturedPhoto[]>([]);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const ghostUrl = useGhostUrl(projectId, issue);

  // Released once, on unmount. The ref follows the latest batch so that
  // cleanup sees every shot, not the empty batch it was created with.
  const shotsRef = useRef<CapturedPhoto[]>([]);
  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);
  useEffect(
    () => () => {
      for (const shot of shotsRef.current) URL.revokeObjectURL(shot.previewUrl);
    },
    [],
  );

  const close = () => {
    if (shots.length > 0) setConfirmingDiscard(true);
    else onCancel();
  };

  // Rendered into <body>, not inside the sheet: the drawer is transformed, and
  // a `fixed` camera inside a transformed ancestor would be pinned to the
  // drawer instead of the screen. The wrapper lifts the camera's own z-[60]
  // above the drawer's z-[70], and turns pointer events back on under the
  // `pointer-events: none` a modal sheet puts on <body>.
  return createPortal(
    <div
      {...{ [ISSUE_CAMERA_ATTR]: "" }}
      className="pointer-events-auto relative z-[80]"
    >
      <CameraCapture
        isOpen
        mode="multi"
        hideModeToggle
        onModeChange={() => {}}
        ghostUrl={ghostUrl}
        reviewLabel="Done"
        batchCount={shots.length}
        latestPreviewUrl={
          shots.length > 0 ? shots[shots.length - 1].previewUrl : null
        }
        onCapture={(shot) => setShots((current) => [...current, shot])}
        onReview={() => onDone(shots.map((shot) => shot.originalFile))}
        onClose={close}
        // Multi mode shows the stack in the library slot, so this is never
        // reached; the fallback below is the camera's only way to the library.
        onPickFromLibrary={onUseLibrary}
        onUseLibrary={onUseLibrary}
      />
      <AlertDialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
        <AlertDialogContent
          {...{ [ISSUE_CAMERA_ATTR]: "" }}
          overlayClassName="z-[90]"
          className="z-[90]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Discard {shots.length} {shots.length === 1 ? "photo" : "photos"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {shots.length === 1
                ? "The photo you just took won't be added to this update."
                : "The photos you just took won't be added to this update."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Go back</AlertDialogCancel>
            <AlertDialogAction className="h-12" onClick={onCancel}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body,
  );
}

/**
 * The ghost: the first photo of the issue's record, as uploaded (before any
 * markup), so arrows drawn on it stay out of the way. Read through the issue,
 * so it shows for a contributor, who may not view records.
 *
 * The issue's cover thumbnail is already on hand and stands in while the
 * record is fetched (a cache hit from the issue page, one request from the
 * list) and the full photo downloads. A record known to have no photos has no
 * ghost at all.
 */
function useGhostUrl(projectId: string, issue: ProjectIssue): string | null {
  const { data: record } = useIssueRecord(projectId, issue.id);
  const first = record ? recordImages(record)[0] : undefined;
  // The untouched original can be a HEIC a browser can't draw; the flattened
  // `image` is always a JPEG or PNG, so it is the fallback.
  const loaded = useFirstLoadable(
    first ? [first.original_url, first.image] : [],
  );
  if (record && !first) return null;
  return loaded ?? issue.record_cover_thumbnail;
}

/**
 * The first of `candidates` that loads as an image, tried in order, or null
 * while none has (yet). Preloads with anonymous CORS for remote URLs — the
 * same mode the ghost `<img>` then renders with — so the swap is served from
 * cache and the preload can't poison the entry for canvas consumers.
 */
function useFirstLoadable(candidates: (string | null | undefined)[]): string | null {
  const urls = candidates.filter((url): url is string => !!url);
  const key = urls.join("\n");
  const [loaded, setLoaded] = useState<{ key: string; url: string } | null>(null);

  useEffect(() => {
    if (!key) return;
    const queue = key.split("\n");
    let cancelled = false;
    let img: HTMLImageElement | null = null;

    const tryNext = () => {
      const url = queue.shift();
      if (!url || cancelled) return;
      img = new Image();
      if (!url.startsWith("blob:")) img.crossOrigin = "anonymous";
      img.onload = () => {
        if (!cancelled) setLoaded({ key, url });
      };
      img.onerror = tryNext;
      img.src = url;
    };
    tryNext();

    return () => {
      cancelled = true;
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };
  }, [key]);

  return loaded?.key === key ? loaded.url : null;
}
